import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AdminIcon from "../Admin/adminIcons";
import { JOINEE_FORM_COLORS, JOINEE_FORM_FONT } from "../../config/joineeFormConstants";

/** Only stored uploads (Cloudinary URLs) or freshly picked files (data/blob URLs) are rendered or downloaded. */
var SAFE_SRC = /^(https?:\/\/|blob:|data:(image\/[a-z0-9.+-]+|application\/pdf)[;,])/i;

var EXT_BY_TYPE = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "application/pdf": ".pdf",
};

var MUTED = "#848F9A";

export function isSafeMediaSrc(src) {
  return typeof src === "string" && SAFE_SRC.test(src.trim());
}

export function isPdfMedia(src, fileName) {
  if (/\.pdf$/i.test(String(fileName || ""))) return true;
  var value = String(src || "");
  return value.indexOf("data:application/pdf") === 0 || /\.pdf(\?|#|$)/i.test(value);
}

export function mediaDownloadName(fileName, fallbackName, type) {
  var name = String(fileName || fallbackName || "file")
    .replace(/[\\/<>:"|?*\x00-\x1F]/g, "_")
    .trim()
    .slice(0, 120);
  if (!name) name = "file";
  if (!/\.[a-z0-9]{2,5}$/i.test(name) && EXT_BY_TYPE[type]) name += EXT_BY_TYPE[type];
  return name;
}

function cloudinaryAttachmentUrl(src) {
  try {
    var url = new URL(src);
    if (url.hostname !== "res.cloudinary.com" || url.pathname.indexOf("/upload/") === -1) return "";
    url.pathname = url.pathname.replace("/upload/", "/upload/fl_attachment/");
    return url.toString();
  } catch (e) {
    return "";
  }
}

function saveBlob(blob, name) {
  var href = URL.createObjectURL(blob);
  var link = document.createElement("a");
  link.href = href;
  link.download = name;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(function () {
    URL.revokeObjectURL(href);
  }, 1000);
}

/** Saves the file under its original name; falls back to opening it in a new tab when the host blocks CORS. */
export function downloadMedia(src, fileName, fallbackName) {
  if (!isSafeMediaSrc(src)) return Promise.resolve(false);
  return fetch(src)
    .then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.blob();
    })
    .then(function (blob) {
      saveBlob(blob, mediaDownloadName(fileName, fallbackName, blob.type));
      return true;
    })
    .catch(function () {
      if (src.indexOf("data:") === 0 || src.indexOf("blob:") === 0) return false;
      window.open(cloudinaryAttachmentUrl(src) || src, "_blank", "noopener,noreferrer");
      return true;
    });
}

function useMediaDownload() {
  var [downloading, setDownloading] = useState(false);
  function download(src, fileName, fallbackName) {
    if (downloading) return;
    setDownloading(true);
    downloadMedia(src, fileName, fallbackName).finally(function () {
      setDownloading(false);
    });
  }
  return { downloading: downloading, download: download };
}

var ACTION_BUTTON_SX = {
  fontFamily: JOINEE_FORM_FONT,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  textTransform: "none",
  borderRadius: "6px",
  minHeight: 36,
  px: 2,
  boxShadow: "none",
  "& .MuiButton-startIcon": { mr: "6px" },
};

var OUTLINED_SX = Object.assign({}, ACTION_BUTTON_SX, {
  color: JOINEE_FORM_COLORS.primary,
  borderColor: JOINEE_FORM_COLORS.primary,
  bgcolor: JOINEE_FORM_COLORS.surface,
  "&:hover": { borderColor: JOINEE_FORM_COLORS.primary, bgcolor: "#E7F1FF" },
});

var CONTAINED_SX = Object.assign({}, ACTION_BUTTON_SX, {
  color: "#fff",
  bgcolor: JOINEE_FORM_COLORS.primary,
  "&:hover": { bgcolor: "#000FB8", boxShadow: "none" },
});

/** Large preview of an uploaded image or PDF with a Download action. */
export function MediaPreviewDialog(props) {
  var media = useMediaDownload();
  var src = isSafeMediaSrc(props.src) ? props.src : "";
  var pdf = isPdfMedia(src, props.fileName);
  var title = props.title || "Preview";

  return (
    <Dialog
      open={Boolean(props.open)}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="media-preview-title"
      PaperProps={{ sx: { borderRadius: "12px", m: "16px", width: "calc(100% - 32px)" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          px: "20px",
          pt: "16px",
          pb: "12px",
          borderBottom: "1px solid " + JOINEE_FORM_COLORS.borderSecondary,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="media-preview-title"
            sx={{ fontFamily: JOINEE_FORM_FONT, fontSize: 18, fontWeight: 600, lineHeight: "26px", color: JOINEE_FORM_COLORS.label }}
          >
            {title}
          </Typography>
          {props.fileName ? (
            <Typography
              noWrap
              title={props.fileName}
              sx={{ fontFamily: JOINEE_FORM_FONT, fontSize: 14, lineHeight: "20px", color: MUTED }}
            >
              {props.fileName}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="Close preview" onClick={props.onClose} sx={{ p: "6px", color: JOINEE_FORM_COLORS.label }}>
          <AdminIcon name="X" size={22} />
        </IconButton>
      </Box>

      <Box sx={{ p: "20px", bgcolor: "#F5F6F7", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
        {!src ? (
          <Typography sx={{ fontFamily: JOINEE_FORM_FONT, fontSize: 15, color: MUTED }}>No file uploaded</Typography>
        ) : pdf ? (
          <Box
            component="iframe"
            title={title}
            src={src}
            sx={{ width: "100%", height: "70vh", border: "1px solid " + JOINEE_FORM_COLORS.border, borderRadius: "6px", bgcolor: "#fff" }}
          />
        ) : (
          <Box
            component="img"
            src={src}
            alt={title}
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              bgcolor: "#fff",
              borderRadius: "6px",
              border: "1px solid " + JOINEE_FORM_COLORS.border,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          px: "20px",
          py: "14px",
          borderTop: "1px solid " + JOINEE_FORM_COLORS.borderSecondary,
        }}
      >
        <Button type="button" variant="outlined" onClick={props.onClose} sx={OUTLINED_SX}>
          Close
        </Button>
        {src ? (
          <Button
            type="button"
            variant="contained"
            disabled={media.downloading}
            onClick={function () {
              media.download(src, props.fileName, props.fallbackName);
            }}
            startIcon={
              media.downloading ? <CircularProgress size={16} color="inherit" /> : <AdminIcon name="Download" size={16} />
            }
            sx={CONTAINED_SX}
          >
            {media.downloading ? "Preparing…" : "Download"}
          </Button>
        ) : null}
      </Box>
    </Dialog>
  );
}

/**
 * View + Download buttons for read-only forms. Rendered as spans so a disabled
 * `<fieldset>` around the form does not disable them.
 */
export function MediaViewActions(props) {
  var media = useMediaDownload();
  if (!isSafeMediaSrc(props.src)) return null;
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pointerEvents: "auto" }}>
      <Button
        component="span"
        variant="outlined"
        onClick={props.onView}
        startIcon={<AdminIcon name="Eye" size={16} />}
        sx={[OUTLINED_SX, { flex: 1, minWidth: 120 }]}
      >
        View
      </Button>
      <Button
        component="span"
        variant="contained"
        aria-disabled={media.downloading}
        onClick={function () {
          media.download(props.src, props.fileName, props.fallbackName);
        }}
        startIcon={media.downloading ? <CircularProgress size={16} color="inherit" /> : <AdminIcon name="Download" size={16} />}
        sx={[CONTAINED_SX, { flex: 1, minWidth: 120 }]}
      >
        {media.downloading ? "Preparing…" : "Download"}
      </Button>
    </Box>
  );
}
